/**
 * The log's timeline past today: the live list's last two days, then a baby's
 * older entries a page at a time ("Show earlier entries"), grouped by day.
 *
 * The live list (polled every 5s) owns everything inside its window. A page
 * fills in only what is older than that window, so an entry a partner removes
 * or changes in the last two days never lingers from a page fetched earlier.
 */
import { isSameLocalDay, monthDay, startOfLocalDay } from "./time.ts";

/** How far back the log's live list reaches. "Last feed" at 1am must still see 11pm. */
export const LIVE_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Rows in one page of a baby's older entries. */
export const PAGE_SIZE = 100;

/** The last row of a page: the next page starts just after it. */
export interface Cursor {
  at: Date;
  id: string;
}

export interface EntryPage<E> {
  entries: E[];
  /** Where the next page starts, or null after the baby's first entry. */
  next: Cursor | null;
}

/**
 * A page of rows, newest first. Its cursor is fixed here, so removing a row
 * from a loaded page later never makes it look like the last one.
 */
export function toPage<E extends { at: Date; id: string }>(
  rows: E[],
  size = PAGE_SIZE,
): EntryPage<E> {
  const last = rows.at(-1);
  return { entries: rows, next: rows.length >= size && last ? { at: last.at, id: last.id } : null };
}

/**
 * The paged entries the live list doesn't cover: older than its window as of
 * its last update (`liveUpdatedAt`, ms), and not in it. Newest first, each once.
 */
export function olderEntries<E extends { at: Date; id: string }>(
  pages: readonly EntryPage<E>[] | undefined,
  live: readonly E[] | undefined,
  liveUpdatedAt: number,
): E[] {
  if (!pages) return [];
  const cutoff = liveUpdatedAt - LIVE_WINDOW_MS;
  const seen = new Set((live ?? []).map((e) => e.id));
  const older: E[] = [];
  for (const e of pages.flatMap((p) => p.entries)) {
    if (e.at.getTime() >= cutoff || seen.has(e.id)) continue;
    seen.add(e.id);
    older.push(e);
  }
  return older.sort((a, b) => b.at.getTime() - a.at.getTime());
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Yesterday", "Sat, Oct 3", or "Wed, Dec 31, 2025" outside this year. */
export function dayLabel(day: Date, now: Date): string {
  if (isSameLocalDay(day, new Date(startOfLocalDay(now).getTime() - 1))) return "Yesterday";
  const label = `${WEEKDAYS[day.getDay()]}, ${monthDay(day)}`;
  return day.getFullYear() === now.getFullYear() ? label : `${label}, ${day.getFullYear()}`;
}

export interface Day<E> {
  /** Local midnight that starts the day. */
  start: Date;
  label: string;
  entries: E[];
}

/** Newest-first entries split into local days, newest day first. */
export function byDay<E extends { at: Date }>(entries: readonly E[], now: Date): Day<E>[] {
  const days: Day<E>[] = [];
  for (const e of entries) {
    const last = days.at(-1);
    if (last && isSameLocalDay(last.start, e.at)) last.entries.push(e);
    else {
      const start = startOfLocalDay(e.at);
      days.push({ start, label: dayLabel(start, now), entries: [e] });
    }
  }
  return days;
}
